import { SupportedLanguageType } from '@/utils/intl';
import { useCurrentLanguage } from '@rhg/hooks/use-current-language';
import enMessages from '@rhg/lang/en.json';
import esMessages from '@rhg/lang/es.json';
import frMessages from '@rhg/lang/fr.json';
import { useTracking } from '@rhg/libs/analytics';
import { ReactNode, useEffect, useRef } from 'react';
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl';

const cache = createIntlCache();

export const localeMessages: Record<
    SupportedLanguageType,
    Record<string, string>
> = {
    en: enMessages,
    es: esMessages,
    fr: frMessages,
};

function useTrackLanguage(currentLanguage: SupportedLanguageType) {
    const isTracked = useRef(false);
    const { setUserProfile } = useTracking();
    useEffect(() => {
        if (!isTracked.current) {
            setUserProfile({ Language: currentLanguage });
            isTracked.current = true;
        }
    }, [setUserProfile, currentLanguage]);
}

export function IntlWrapper({ children }: { children?: ReactNode }) {
    const currentLanguage = useCurrentLanguage();
    useTrackLanguage(currentLanguage);

    const messages =
        // When developing translations get rid of this NODE_ENV check, otherwise leave this in place
        // SEE: https://github.com/calm/evexia-web/blob/main/apps/evexia/README.md#i18n
        process.env.NODE_ENV === 'development'
            ? {}
            : localeMessages[currentLanguage] || localeMessages.en;

    const intl = createIntl(
        {
            locale: currentLanguage,
            messages,
        },
        cache
    );

    return <RawIntlProvider value={intl}>{children}</RawIntlProvider>;
}
